import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { fetchAuthUserRoutine } from 'features/auth/ducks/actions'
import { setLocationConsent as setLocationConsentApi } from 'services/AuthService'

const LocationSharingPanel = () => {
  const dispatch = useDispatch()
  const user = useSelector(getCurrentUser)
  const [saving, setSaving] = useState(false)

  const consent = user?.locationConsent
    || (typeof localStorage !== 'undefined' ? localStorage.getItem('locationConsent') : null)
  const enabled = consent === 'granted'
  const declined = consent === 'declined'

  const change = async choice => {
    if (saving) return
    setSaving(true)
    try {
      try { if (typeof localStorage !== 'undefined') localStorage.setItem('locationConsent', choice) } catch (e) {}
      await setLocationConsentApi(choice)
      dispatch(fetchAuthUserRoutine())
      toast.success(choice === 'granted' ? 'Location sharing turned on.' : 'Location sharing turned off.')
    } catch (e) {
      toast.error('Could not update your preference.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Wrap>
      <Description>
        When you submit a task form, we can record your device's GPS location at that
        moment to evidence that on-site work was carried out at the building. Sharing is
        optional, does not affect your use of the platform, and you can change it here at
        any time.
      </Description>

      <Row>
        <StateText>
          Location sharing is <strong>{enabled ? 'on' : 'off'}</strong>
          {consent == null && <Muted> — you'll be asked the first time you submit a form</Muted>}
        </StateText>
        <Toggle>
          <ToggleBtn $active={enabled} disabled={saving || enabled} onClick={() => change('granted')}>On</ToggleBtn>
          <ToggleBtn $active={declined} disabled={saving || declined} onClick={() => change('declined')}>Off</ToggleBtn>
        </Toggle>
      </Row>

      <Hint>
        What's collected: GPS coordinates (latitude and longitude) and their approximate
        accuracy, only at the moment you submit a form. Lawful basis: your consent.
      </Hint>
    </Wrap>
  )
}

export default LocationSharingPanel

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Description = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.lighterGrey || '#f8f9fa'};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
`

const StateText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};

  strong { font-weight: 700; }
`

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.grey[200]};
`

const Toggle = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
`

const ToggleBtn = styled.button`
  font: inherit;
  border: none;
  background: ${({ $active, theme }) => ($active ? theme.colors.secondary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.haiti)};
  padding: 7px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { cursor: default; }
  &:not(:disabled):hover { background: ${({ $active, theme }) => ($active ? theme.colors.secondary : theme.colors.lighterGrey)}; }
`

const Hint = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700] || '#475569'};
`
